import { Check, Minus, X } from 'lucide-react';
import type { AgreementVerifyResult } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';

type Outcome = boolean | null;

/** One tick, cross or dash with words, so it never relies on colour alone. */
export function CheckRow({ label, outcome, explanation }: { label: string; outcome: Outcome; explanation: string }) {
  const word = outcome === null ? t('agreementsUi.verify.skipped') : outcome ? t('agreementsUi.verify.passed') : t('agreementsUi.verify.failed');
  const Icon = outcome === null ? Minus : outcome ? Check : X;
  const tone = outcome === null ? 'text-slate-600' : outcome ? 'text-green-800' : 'text-red-800';
  return (
    <li className="flex items-start gap-3 py-2">
      <Icon aria-hidden="true" className={`mt-0.5 h-5 w-5 shrink-0 ${tone}`} />
      <div>
        <div className="font-medium">
          {label}: <span className={tone}>{word}</span>
        </div>
        <div className="text-sm text-slate-700">{explanation}</div>
      </div>
    </li>
  );
}

/** The three checks from POST /agreements/:id/verify, with the timestamp line and any problems found. */
export function VerifyPanel({ result }: { result: AgreementVerifyResult }) {
  const fp = result.fingerprintMatches;
  const sig = result.signatureValid;
  return (
    <section aria-label={t('agreementsUi.verify.title')} className="rounded-lg border border-slate-200 p-4">
      <h3 className="mb-1 font-semibold">{t('agreementsUi.verify.title')}</h3>
      <ul className="divide-y divide-slate-100">
        <CheckRow
          label={t('agreementsUi.verify.fingerprint')}
          outcome={fp}
          explanation={t(fp === null ? 'agreementsUi.verify.fingerprint.na' : fp ? 'agreementsUi.verify.fingerprint.ok' : 'agreementsUi.verify.fingerprint.bad')}
        />
        <CheckRow
          label={t('agreementsUi.verify.chain')}
          outcome={result.chainUnbroken}
          explanation={result.chainUnbroken ? t('agreementsUi.verify.chain.ok', { seq: result.checkedThroughSeq }) : t('agreementsUi.verify.chain.bad')}
        />
        <CheckRow
          label={t('agreementsUi.verify.signature')}
          outcome={sig}
          explanation={t(sig === null ? 'agreementsUi.verify.signature.na' : sig ? 'agreementsUi.verify.signature.ok' : 'agreementsUi.verify.signature.bad')}
        />
      </ul>
      <p className="mt-2 text-sm text-slate-700">
        {result.timestampedAt ? t('agreementsUi.verify.timestamp', { when: formatDateTime(result.timestampedAt) }) : t('agreementsUi.verify.noTimestamp')}
      </p>
      {result.problems.length > 0 ? (
        <div role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <div className="font-medium">{t('agreementsUi.verify.problems')}</div>
          <ul className="list-disc pl-5">
            {result.problems.map((p, i) => (
              <li key={`${p.seq ?? 'x'}-${i}`}>
                {p.seq === null
                  ? t('agreementsUi.verify.problemNoSeq', { message: t(p.messageKey) })
                  : t('agreementsUi.verify.problem', { seq: p.seq, message: t(p.messageKey) })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
